import {
    AiToolCallHistoryItem,
    AiToolCallStatus,
    AiToolOutputCounts,
    AiToolOutputSummary,
    PortalChatMessage,
} from 'types/aiChat';

const TOOL_STATE_TO_STATUS: Record<string, AiToolCallStatus> = {
    'output-available': 'success',
    'output-error': 'error',
    'output-denied': 'error',
    'input-streaming': 'pending',
    'input-available': 'pending',
};

export interface ToolPart {
    type: string;
    toolName?: string;
    toolCallId: string;
    state: string;
    input?: unknown;
    output?: unknown;
    errorText?: string;
}

export const isToolPart = (part: PortalChatMessage['parts'][number]) => {
    if (part.type === 'dynamic-tool') return true;
    return part.type.startsWith('tool-');
};

export const getToolName = (part: ToolPart): string => {
    if (part.type === 'dynamic-tool') return part.toolName || 'tool';
    return part.type.replace(/^tool-/, '') || 'tool';
};

const getToolStatus = (part: ToolPart): AiToolCallStatus => {
    if (TOOL_STATE_TO_STATUS[part.state]) {
        return TOOL_STATE_TO_STATUS[part.state];
    }

    const normalizedState = (part.state || '').toLowerCase();

    if (normalizedState.startsWith('output-')) {
        if (normalizedState.includes('error') || normalizedState.includes('denied')) {
            return 'error';
        }
        return 'success';
    }

    if (
        normalizedState.startsWith('input-') ||
        normalizedState.includes('pending') ||
        normalizedState.includes('running')
    ) {
        return 'pending';
    }

    if (part.errorText) {
        return 'error';
    }

    if (part.output !== undefined) {
        return 'success';
    }

    return 'pending';
};

/** A live output's summary: what the backend sends in place of an output that stays on the server. */
const summaryOf = (output: unknown): AiToolOutputSummary | undefined => {
    if (!output || typeof output !== 'object' || !('count' in output)) {
        return undefined;
    }
    const { count, partial, total, counts } = output as Record<string, unknown>;
    return {
        count: typeof count === 'number' ? count : null,
        partial: partial === true,
        total: typeof total === 'number' ? total : null,
        ...(counts && typeof counts === 'object' ? { counts: counts as AiToolOutputCounts } : {}),
    };
};

const toHistoryItem = (part: ToolPart, summaries?: Record<string, AiToolOutputSummary>): AiToolCallHistoryItem => {
    const summary = summaries?.[part.toolCallId] ?? summaryOf(part.output);
    return {
        id: part.toolCallId || `${getToolName(part)}-${part.state}`,
        name: getToolName(part),
        state: part.state,
        status: getToolStatus(part),
        input: part.input,
        output: part.output,
        errorText: part.errorText,
        ...(summary ? { summary } : {}),
    };
};

/**
 * The message's tool calls in arrival order, one per call id. `summaries` are a reloaded chat's (its metadata's
 * `toolSummaries`); live, each call's summary is its output.
 */
export const extractToolCallHistory = (
    parts: PortalChatMessage['parts'],
    summaries?: Record<string, AiToolOutputSummary>
): AiToolCallHistoryItem[] => {
    const dedupe = new Map<string, AiToolCallHistoryItem>();
    const order: string[] = [];

    parts.forEach((part) => {
        if (!isToolPart(part)) return;
        const item = toHistoryItem(part as unknown as ToolPart, summaries);
        const existing = dedupe.get(item.id);

        if (!existing) {
            order.push(item.id);
            dedupe.set(item.id, item);
            return;
        }

        // Terminal states must not be downgraded to pending due to out-of-order parts.
        if ((existing.status === 'success' || existing.status === 'error') && item.status === 'pending') {
            return;
        }

        dedupe.set(item.id, item);
    });

    return order.map((id) => dedupe.get(id)).filter((item): item is AiToolCallHistoryItem => !!item);
};
